import { Injectable } from '@angular/core';
import { type Observable } from 'rxjs';

import { BaseHttpService } from '@common/services/REST/base.http.service';
import { type ShopCartIconSettingsType } from '@common/types/shop-cart-icon-settings.type';
import type { MiniCartSettings, MiniCartSiteSettings } from '@common/types/mini-cart-settings.type';
import type { MiniCartButtonStyles, SiteSettingsMiniCartStyles } from '@common/types/site.type';
import { map } from 'rxjs/operators';
import { EButtonStyleType, EButtonWidthType, ECartIconType, ESize } from '@common/enums';
import { EIconPosition } from '@common/enums/icon-position.enum';
import type { FormButtonSettings } from '@components/sp-form-button-settings';
import {
    DEFAULT_BUTTON_ADDITIONAL_EFFECTS,
    DEFAULT_BUTTON_BASE,
    DEFAULT_BUTTON_HOVER_STATES,
    DEFAULT_BUTTON_ICON,
} from '@common/constants';

function defaultButtonValue(label: string): FormButtonSettings {
    return {
        label,
        styleType: EButtonStyleType.primary,
        size: ESize.M,
        buttonBase: { ...DEFAULT_BUTTON_BASE },
        buttonHover: { ...DEFAULT_BUTTON_HOVER_STATES },
        widthType: EButtonWidthType.full,
        horizontalFormWidthType: EButtonWidthType.auto,
        widthValue: 260,
        buttonIcon: DEFAULT_BUTTON_ICON,
        additionalEffects: DEFAULT_BUTTON_ADDITIONAL_EFFECTS,
    };
}

function mapMiniCartButtonStylesToApi(styles: FormButtonSettings): MiniCartButtonStyles {
    return {
        label: styles.label,
        size: styles.size,
        style_type: styles.styleType,
        button_base: styles.buttonBase,
        button_hover: styles.buttonHover,
        width_type: styles.widthType,
        horizontal_form_width_type: styles.horizontalFormWidthType,
        width_value: styles.widthValue,
        button_icon: styles.buttonIcon,
        additional_effects: styles.additionalEffects,
        is_independent_corners: styles.isIndependentCorners,
        advanced_radius: styles.advancedRadius,
        advanced_border_settings: styles.advancedBorderSettings,
        is_shadow: styles.isShadow,
        shadow_settings: styles.shadowSettings,
        is_show_button: styles.isShowButton,
    };
}

function mapMiniCartStylesToApi(styles: MiniCartSettings): SiteSettingsMiniCartStyles {
    return {
        modal_background_color: styles.modalBackgroundColor,
        modal_border_radius: styles.modalBorderRadius,
        separator_color: styles.separatorColor,
        text_color: styles.textColor,
        price_color: styles.priceColor,
        old_price_color: styles.oldPriceColor,
        delete_button_icon_color: styles.deleteButtonIconColor,
        delete_button_bg_color: styles.deleteButtonBgColor,
        count_button_text_color: styles.countButtonTextColor,
        count_button_bg_color: styles.countButtonBgColor,
        count_button_border_color: styles.countButtonBorderColor,
        count_button_rounding: styles.countButtonRounding,
        button_bg_color: styles.buttonBgColor,
        button_text_color: styles.buttonTextColor,
        button_border_radius: styles.buttonBorderRadius,
        button_settings: mapMiniCartButtonStylesToApi(styles.buttonSettings),
    };
}

function mapMiniCartButtonStylesFromApi(styles: MiniCartButtonStyles): Partial<FormButtonSettings> {
    return {
        label: styles.label,
        size: styles.size,
        styleType: styles.style_type ?? EButtonStyleType.primary,
        buttonBase: styles.button_base ?? { ...DEFAULT_BUTTON_BASE },
        buttonHover: styles.button_hover ?? { ...DEFAULT_BUTTON_HOVER_STATES },
        widthType: styles.width_type,
        horizontalFormWidthType: styles.horizontal_form_width_type,
        widthValue: styles.width_value,
        buttonIcon: styles.button_icon,
        additionalEffects: styles.additional_effects,
        isIndependentCorners: styles.is_independent_corners,
        advancedRadius: styles.advanced_radius,
        advancedBorderSettings: styles.advanced_border_settings,
        isShadow: styles.is_shadow,
        shadowSettings: styles.shadow_settings,
        isShowButton: styles.is_show_button,
    };
}

export function mapMiniCartStylesFromApi(styles: SiteSettingsMiniCartStyles | null): MiniCartSettings {
    return {
        modalBackgroundColor: styles?.modal_background_color ?? '#ffffff',
        modalBorderRadius: styles?.modal_border_radius ?? 8,
        separatorColor: styles?.separator_color ?? '#f3f3f3',
        textColor: styles?.text_color ?? '#1d1c1c',
        priceColor: styles?.price_color ?? '#1d1c1c',
        oldPriceColor: styles?.old_price_color ?? '#e87651',
        deleteButtonIconColor: styles?.delete_button_icon_color ?? '#1d1c1c',
        deleteButtonBgColor: styles?.delete_button_bg_color ?? '#f3f3f3',
        countButtonTextColor: styles?.count_button_text_color ?? '#1d1c1c',
        countButtonBgColor: styles?.count_button_bg_color ?? '#ffffff',
        countButtonBorderColor: styles?.count_button_border_color ?? '#d6e3e3',
        countButtonRounding: styles?.count_button_rounding ?? 4,
        buttonBgColor: styles?.button_bg_color ?? '#1d1c1c',
        buttonTextColor: styles?.button_text_color ?? '#ffffff',
        buttonBorderRadius: styles?.button_border_radius ?? 4,
        buttonSettings: styles?.button_settings
            ? (mapMiniCartButtonStylesFromApi(styles.button_settings) as FormButtonSettings)
            : defaultButtonValue('mini_cart_checkout_button'),
    };
}

@Injectable({
    providedIn: 'root',
})
export class ShopCartIconHttpService extends BaseHttpService {
    public setIconStyle(siteId: string, data: ShopCartIconSettingsType, isDraft: boolean): Observable<any> {
        return this.http.post<any>(this.formatApiUrl(`sites/${siteId}/settings`), {
            shop: {
                [isDraft ? 'cart_icon_draft' : 'cart_icon']: {
                    icon_bg_color: data.iconBgColor,
                    icon_color: data.iconColor,
                    icon_counter_bg_color: data.iconCounterBgColor,
                    icon_counter_color: data.iconCounterColor,
                    icon_size: data.iconSize,
                    icon_type: data.iconType,
                    icon_rounding: data.iconRounding,
                    icon_position_type: data.iconPositionType,
                },
            },
        });
    }

    public setMiniCartSettings(siteId: string, enabled: boolean, styles?: MiniCartSettings): Observable<any> {
        return this.http.post<any>(this.formatApiUrl(`sites/${siteId}/settings`), {
            shop: {
                mini_cart: {
                    enabled,
                    ...(styles ? { styles: mapMiniCartStylesToApi(styles) } : {}),
                },
            },
        });
    }

    public getMiniCartSettings(siteId: string): Observable<MiniCartSiteSettings> {
        return this.http.get<any>(this.formatApiUrl(`sites/${siteId}/settings`)).pipe(
            map((res) => {
                const miniCart = res?.data?.shop?.mini_cart;
                return {
                    miniCartEnabled: !!miniCart?.enabled,
                    miniCartStyles: miniCart?.styles ? mapMiniCartStylesFromApi(miniCart.styles) : null,
                };
            })
        );
    }

    public getIconStyle(siteId: string): Observable<{ data: ShopCartIconSettingsType; isCartEnabled: boolean }> {
        return this.http.get<any>(this.formatApiUrl(`sites/${siteId}/settings`)).pipe(
            map((res) => {
                const data = res?.data?.shop?.cart_icon;
                const dataDraft = res?.data?.shop?.cart_icon_draft;
                const iconStyles =
                    dataDraft === null || JSON.stringify(data) === JSON.stringify(dataDraft) ? data : dataDraft;
                return {
                    data: {
                        iconBgColor: iconStyles ? iconStyles.icon_bg_color : '#FFFFFF',
                        iconColor: iconStyles ? iconStyles.icon_color : '#1D1C1C',
                        iconCounterBgColor: iconStyles ? iconStyles.icon_counter_bg_color : '#FF2525',
                        iconCounterColor: iconStyles ? iconStyles.icon_counter_color : '#FFFFFF',
                        iconSize: iconStyles ? iconStyles.icon_size : ESize.M,
                        iconType: iconStyles ? iconStyles.icon_type : ECartIconType.ICON_1,
                        iconRounding: iconStyles ? iconStyles.icon_rounding : 30,
                        iconPositionType: iconStyles.icon_position_type
                            ? iconStyles.icon_position_type
                            : EIconPosition.SITE,
                    },
                    isCartEnabled: !!res?.data?.shop.enabled,
                };
            })
        );
    }
}
